import { Button, Flex, Spacing, Text, Tooltip, useModal } from '@billy/design-system'
import { trackError } from '@billy/track-error'
import { ReactElement, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ModalId } from '../../../../../../enums/modalId'
import { useFecImportSessionContext } from '../../contexts/fecImportSessionContext'
import { useDeleteFecImport } from '../../hooks/useDeleteFecImport'
import type { FecImportItem } from '../../query-api'
import { canDeleteFecImports } from '../../utils/canDeleteFecImports'
import { formatFecImportPeriod } from '../../utils/formatFecImportPeriod'
import { FecImportFileOutput } from '../FecImportConfirmationStep/elements/FecImportFileOutput'
import { FecImportResumeDeleteModal } from './elements/FecImportResumeDeleteModal'
import * as Styled from './styles'

interface FecImportResumeStepProps {
  fecImports: FecImportItem[]
}

export const FecImportResumeStep = ({ fecImports }: FecImportResumeStepProps): ReactElement => {
  const { i18n, t } = useTranslation()
  const { onResumeImports } = useFecImportSessionContext()
  const { deleteMany } = useDeleteFecImport()
  const { open: openDeleteModal } = useModal(ModalId.FecImportResumeDeleteModal)
  const [isResuming, setIsResuming] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const handleReviewMappingsClick = useCallback(async () => {
    setIsResuming(true)

    try {
      await onResumeImports(fecImports.map((fecImport) => fecImport.id))
    } finally {
      setIsResuming(false)
    }
  }, [fecImports, onResumeImports])

  const handleDeleteConfirm = useCallback(async () => {
    setIsDeleting(true)

    try {
      await deleteMany(fecImports.map((fecImport) => fecImport.id))
    } catch (error) {
      trackError(
        new Error(`FEC import delete failed: ${error instanceof Error ? error.message : String(error)}`, {
          cause: error,
        }),
      )

      // The modal shows its own error for it.
      throw error
    } finally {
      setIsDeleting(false)
    }
  }, [deleteMany, fecImports])

  const canDelete = canDeleteFecImports(fecImports)
  const deleteButton = (
    <Button disabled={!canDelete || isResuming || isDeleting} onClick={openDeleteModal} variant="outlined">
      {t('settings.organization.accounting.fec_file_import.modal.resume_step.delete_button')}
    </Button>
  )

  return (
    <Styled.ResumeContainer>
      <Flex flexDirection="column" gap={Spacing.S20}>
        <Flex flexDirection="column" gap={Spacing.S8}>
          <Text colorVariant="primary" variant="h2">
            {t('settings.organization.accounting.fec_file_import.modal.resume_step.title')}
          </Text>
          <Text colorVariant="secondary" variant="bodyMD">
            {t('settings.organization.accounting.fec_file_import.modal.resume_step.description')}
          </Text>
        </Flex>
        <Flex flexDirection="column" gap={Spacing.S10}>
          {fecImports.map((fecImport) => (
            <FecImportFileOutput
              fileName={fecImport.fileName}
              key={fecImport.id}
              period={formatFecImportPeriod(fecImport.periodStart, fecImport.periodEnd, i18n.language)}
            />
          ))}
        </Flex>
        <Flex gap={Spacing.S10} justifyContent="flex-end">
          {canDelete ? (
            deleteButton
          ) : (
            <Tooltip
              label={t('settings.organization.accounting.fec_file_import.modal.resume_step.delete_locked_tooltip')}
              placement="top"
            >
              <Tooltip.DisabledWrapper>{deleteButton}</Tooltip.DisabledWrapper>
            </Tooltip>
          )}
          <Button
            disabled={isDeleting}
            iconRight="arrowRight"
            loading={isResuming}
            onClick={handleReviewMappingsClick}
            variant="filled"
          >
            {t('settings.organization.accounting.fec_file_import.modal.resume_step.review_mappings_button')}
          </Button>
        </Flex>
      </Flex>
      <FecImportResumeDeleteModal isDeleting={isDeleting} onConfirm={handleDeleteConfirm} />
    </Styled.ResumeContainer>
  )
}
